import type { CardSkinOptions } from "./card-skin-fields"
import { getFontCssUrl } from "../utils"
import { HEX_COLOR_RE } from "../zod-lenient"

/**
 * HomeRunner Skin card template, "Classic" preset (#872). Pure builder (same
 * pattern as buildMapLayoutCss / buildSearchbarCss) so every derivation is
 * unit-testable without a DOM.
 *
 * Port of the toolkit's `card` control group (includes/HomerunnerSkin/
 * Preset.php + assets/css/homerunner-skin.css). The toolkit ships no preset
 * named "Classic" — its built-ins are `default` and `editorial-square` — and
 * default.json calls itself a "Theme-neutral baseline… All flags neutral so
 * structural HR-plugin defaults are preserved". Per #870 decision 4 that base
 * IS Classic, which is why Classic costs zero bytes of CSS here:
 *
 *   THE CONTRACT: a neutral (default) value emits NOTHING. buildCardSkinCss({})
 *   returns ''. An unconfigured card is therefore byte-identical to the card
 *   this widget rendered before the group existed — the toolkit reaches the
 *   same outcome by only emitting a body-modifier class for non-neutral flags.
 *
 * Specificity: these rules are injected into the consuming widget's own
 * <style> alongside the shared card CSS, so each selector is prefixed with the
 * caller's `root` (the explorer's `.hfse-explorer-container` by default,
 * `.hr-related` for related-properties, a per-instance scope for the dashboard
 * preview — #902) to outrank the base stylesheet's 1-2 class rules without
 * !important. The one exception is the title font-family, which must beat
 * useExplorerCss's own `.hfse * { font-family: … !important }`; the other
 * roots have no such rule, and the !important is harmless there.
 *
 * Injection safety: no free text reaches the stylesheet. Sizes are schema-
 * validated integers, colors are hex-validated, and fonts/shadows are enum
 * keys looked up in the tables below.
 *
 * #902: every knob that has a meaningful surface on the shared property-card
 * templates (the grid's default since card.design landed) also emits an
 * .hr-pc-* rule, so the panel styles whatever card the grid renders. Four
 * knobs stay Legacy-only by construction — metaLayout and showTypeIcon (the
 * templates have no type+location row; the chip owns type), metaSpecStyle
 * (templates render text specs natively) and subtitleFontSize (no subtitle
 * node) — and emit nothing for the template family.
 */

/** Skin FONT_FAMILY_PRESETS, keyed by the schema's enum slugs. */
const TITLE_FONTS: Record<string, string> = {
  system: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
  'modern-sans': 'Inter, system-ui, sans-serif',
  'classic-sans': 'Helvetica, Arial, sans-serif',
  geometric: '"Avenir Next", Avenir, Montserrat, sans-serif',
  humanist: '"Source Sans Pro", "Open Sans", sans-serif',
  serif: 'Georgia, "Times New Roman", serif',
  'display-serif': '"Playfair Display", Georgia, serif',
  editorial: '"Crimson Pro", "Libre Caslon Text", Georgia, serif',
  // #1316: Editorial Square preset exact match. The toolkit's
  // editorial-square.json sets hrtk-skin-font-family-title to literally
  // 'Tenor Sans', serif — this stack is that value verbatim, replacing the
  // `geometric` approximation the preset shipped with in #1208/#1315.
  'tenor-sans': '"Tenor Sans", serif',
}

/**
 * Google-hosted family to webfont-load for each title preset (#1219). The
 * CSS stacks above mix OS-native names (Segoe UI, Helvetica, Georgia — every
 * browser ships these) with the one specific non-system family the toolkit's
 * preset was actually built around (Inter, Montserrat, "Source Sans Pro",
 * "Playfair Display", "Crimson Pro", "Tenor Sans") — that family is the ONE thing worth
 * requesting from Google Fonts per preset, never the whole stack. `system`,
 * `classic-sans`, and `serif` are built entirely from stock OS fonts and
 * intentionally have no entry — they request nothing.
 */
const TITLE_FONT_GOOGLE_FAMILY: Partial<Record<string, string>> = {
  'modern-sans': 'Inter',
  geometric: 'Montserrat',
  humanist: 'Source Sans Pro',
  'display-serif': 'Playfair Display',
  editorial: 'Crimson Pro',
  'tenor-sans': 'Tenor Sans', // #1316: Editorial Square exact font
}

/**
 * The Google Fonts family to webfont-load for a card's title preset, or
 * `null` when the preset needs nothing (unset, `inherit`, or one of the
 * system-safe stacks) — mirrors the `titleFontFamily !== 'inherit'` gate in
 * buildCardSkinCss below. Consumed by ExplorerContent to feed the widget's
 * existing WebFont/webfontloader mechanism (option (a), #1219).
 */
export function getCardSkinTitleGoogleFont(titleFontFamily: string | undefined): string | null {
  if (!titleFontFamily || titleFontFamily === 'inherit') return null
  return TITLE_FONT_GOOGLE_FAMILY[titleFontFamily] ?? null
}

/** Skin SHADOW_PRESETS. `default` is absent on purpose — it emits nothing. */
const CARD_SHADOWS: Record<string, string> = {
  none: 'none',
  subtle: '0 1px 2px rgba(0,0,0,.05)',
  card: '0 4px 12px rgba(0,0,0,.08)',
  pop: '0 8px 24px rgba(0,0,0,.12)',
  dramatic: '0 16px 40px rgba(0,0,0,.18)',
}

/** A sizing leaf is "set" only when it parsed to a real integer (absent = inherit). */
const isPx = (v: number | undefined): v is number => typeof v === 'number' && Number.isFinite(v)
// Colours are interpolated into a <style> sink, so they are re-checked HERE,
// not only in the schema: since #902 the parameter is the structural
// CardSkinOptions, which any object with a string `priceColor` satisfies, so
// the compile-time push through the lenient parse is gone. A non-hex value
// emits nothing rather than `#fff} .x{display:none}`.
const isHex = (v: string | undefined): v is string => typeof v === 'string' && HEX_COLOR_RE.test(v)

/**
 * Every rule this builder emits is scoped under `root`, which defaults to the
 * explorer's own container so existing callers are unchanged byte-for-byte.
 *
 * #902 bullet 5: related-properties renders the SAME legacy card component
 * (common/property-card/TemplateExplorer) inside `.hr-related`, which no
 * `.hfse-explorer-container` ancestor ever wraps — so before the root became a
 * parameter, the 26 shared skin knobs could not reach it at all. The dashboard's
 * Card Template preview passes its own per-instance scope class for the same
 * reason (common/property-card/skin-preview.tsx).
 *
 * `card` is a PARTIAL: related-properties declares the shared skin leaves only,
 * with none of the explorer's display or `loadMore*` keys. Every read below is
 * already guarded (a neutral or absent value emits nothing), so an absent key
 * behaves exactly like a neutral one — which is what keeps the Load More rules
 * out of a widget that has no Load More button.
 */
export function buildCardSkinCss(
  card: CardSkinOptions | undefined,
  root = '.hfse-explorer-container',
): string {
  if (!card) return ''
  const S = root
  // Template family (#902): the shared property-card templates the grid
  // renders for card.design classic/overlay/detailed. Same neutral contract —
  // every rule below emits only for a non-neutral value, so the .hr-pc-* pairs
  // ride the same conditionals as their .hfse-* twins. Sizing knobs prefer the
  // family's own --hr-pc-* custom properties (property-card.css resolves them
  // everywhere, scrim included) over selector fights whose winner would depend
  // on stylesheet injection order.
  const T = `${S} .hr-property-card`
  // Structural knobs skip the Overlay template: its content lives on the photo
  // scrim, where none of these body orders exist.
  const TC = `${T} .hr-pc-card:not(.hr-pc-card--overlay)`
  const parts: string[] = []

  // ── Type ────────────────────────────────────────────────────────────────
  if (card.titleFontFamily && card.titleFontFamily !== 'inherit') {
    const stack = TITLE_FONTS[card.titleFontFamily]
    // !important is required here and nowhere else: useExplorerCss emits
    // `.hfse * { font-family: <widget font> !important }`, which no specificity
    // can beat.
    if (stack) {
      parts.push(
        `${S} .hfse-property-title { font-family: ${stack} !important; }`,
        `${T} .hr-pc-title { font-family: ${stack} !important; }`,
      )
    }
  }
  if (isPx(card.titleFontSize)) {
    // 2 classes so it outranks `.hk-h5`'s 1rem.
    parts.push(
      `${S} .hfse-property-title { font-size: ${card.titleFontSize}px; }`,
      `${T} { --hr-pc-fs-title: ${card.titleFontSize}px; }`,
    )
  }
  if (card.titleTextTransform && card.titleTextTransform !== 'none') {
    parts.push(
      `${S} .hfse-property-title { text-transform: ${card.titleTextTransform}; }`,
      `${T} .hr-pc-title { text-transform: ${card.titleTextTransform}; }`,
    )
  }
  if (isPx(card.subtitleFontSize)) {
    parts.push(`${S} .hfse-property-subtitle { font-size: ${card.subtitleFontSize}px; }`)
  }
  if (isPx(card.priceFontSize)) {
    // explorer.css sizes the container (14px) and the strong amount (16px)
    // separately, so sizing only the container would leave the amount frozen
    // and the control looking broken. The +2px step preserves that ratio.
    // The template family scales its amount by em (1.19em on the b), so the
    // var alone keeps the same ratio there.
    parts.push(
      `${S} .hfse-property-price-container { font-size: ${card.priceFontSize}px; }`,
      `${S} .hfse-property-price strong { font-size: ${card.priceFontSize + 2}px; }`,
      `${T} { --hr-pc-fs-price: ${card.priceFontSize}px; }`,
    )
  }
  if (isHex(card.priceColor)) {
    // Template mapping mirrors what .hfse-property-price actually covers: the
    // PRIMARY line only — single-price branches (plain p), the per-night
    // cluster, and a quote line with no per-night cluster (total mode, where
    // the total IS the primary line). The __total span and the notes stay
    // uncolored, as on the Legacy card. Scoped through .hr-pc-body (fresh
    // pass): every non-scrim price lives in a body — Overlay's no-photo
    // fallback included — while the photographed Overlay scrim, which has no
    // body, keeps its white-on-photo price deterministically rather than by
    // injection-order coin flip against property-card.css. The b needs naming
    // (it carries its own --hfse-foreground) just like the legacy strong does.
    const PB = `${T} .hr-pc-body`
    parts.push(
      `${S} .hfse-property-price, ${S} .hfse-property-price strong { color: ${card.priceColor}; }`,
      `${PB} p.hr-pc-price, ${PB} p.hr-pc-price b,
${PB} .hr-pc-price__pernight, ${PB} .hr-pc-price__pernight b,
${PB} .hr-pc-price__line:not(:has(.hr-pc-price__pernight)),
${PB} .hr-pc-price__line:not(:has(.hr-pc-price__pernight)) b { color: ${card.priceColor}; }`,
    )
  }
  if (isPx(card.specFontSize)) {
    // explorer.css pins these items to height:28px/line-height:20px, which
    // would clip a larger size — the toolkit frees both for the same reason.
    parts.push(`${S} .hfse-property-meta-secondary .hfse-property-guests,
${S} .hfse-property-meta-secondary .hfse-property-bedrooms,
${S} .hfse-property-meta-secondary .hfse-property-bathrooms {
  font-size: ${card.specFontSize}px;
  line-height: 1.4;
  height: auto;
}`)
    parts.push(`${T} { --hr-pc-fs-meta: ${card.specFontSize}px; }`)
  }

  // ── Shape ───────────────────────────────────────────────────────────────
  if (isPx(card.radius)) {
    // overflow only when there is a curve to clip, so a 0 radius stays a
    // no-op rather than newly clipping anything.
    const clip = card.radius > 0 ? ' overflow: hidden;' : ''
    parts.push(`${S} .hfse-property-card { border-radius: ${card.radius}px;${clip} }`)
    // The template shell, its stretched link and its focus ring all read
    // --hr-pc-radius, and the card already clips (overflow: hidden), so the
    // var carries the whole knob — 0 included (the template default is 12px).
    parts.push(`${T} { --hr-pc-radius: ${card.radius}px; }`)
  }
  // Blank slideshow radius follows the card radius (toolkit:
  // `var(--hrtk-skin-radius-slideshow, var(--hrtk-skin-radius-card))`).
  const photoRadius = isPx(card.slideshowRadius) ? card.slideshowRadius : card.radius
  if (isPx(photoRadius) && photoRadius > 0) {
    parts.push(`${S} .hfse-property-slideshow,
${S} .hfse-property-card .hk-slideshow-items,
${S} .hfse-property-card .hk-slideshow-items > li,
${S} .hfse-property-card .hfse-property-slideshow-image {
  border-radius: ${photoRadius}px;
}`)
  }
  // Template media is flush inside the shell and the card's own clip already
  // rounds its outer corners, so only an EXPLICIT photo radius emits here —
  // following the card radius (the legacy fallback above) would newly round
  // the media's bottom edge mid-card on every radius change.
  if (isPx(card.slideshowRadius) && card.slideshowRadius > 0) {
    parts.push(`${T} .hr-pc-media { border-radius: ${card.slideshowRadius}px; overflow: hidden; }`)
  }
  if (card.shadow && card.shadow !== 'default') {
    const shadow = CARD_SHADOWS[card.shadow]
    // Hover gets the same value: leaving explorer.css's 0 14px 25px hover in
    // place under a "subtle"/"none" resting shadow reads as a bug. Same on
    // the template shell, whose hover keeps its translateY lift either way.
    if (shadow) {
      parts.push(
        `${S} .hfse-property-card, ${S} .hfse-property-card:hover { box-shadow: ${shadow}; }`,
        `${T} .hr-pc-card, ${T} .hr-pc-card:hover { box-shadow: ${shadow}; }`,
      )
    }
  }

  // ── Image ───────────────────────────────────────────────────────────────
  // imageAspectRatio stays an inline style on the slideshow (PropertyCard) —
  // it already worked that way; the schema only gained the Skin's 3/4 option.
  if (isPx(card.dotnavSize)) {
    // Explorer dots: the transparent border grows or shrinks with the dot so
    // the button stays a 24px touch target (#1865, explorer-card.css).
    // Template dots: size the visible ::before only — the 24px touch target
    // around it stays.
    parts.push(`${S} .hfse-property-container .hk-dotnav > * > * {
  width: ${card.dotnavSize}px;
  height: ${card.dotnavSize}px;
  border-width: ${Math.max(0, Math.ceil((24 - card.dotnavSize) / 2))}px;
}`)
    parts.push(`${T} .hr-pc-media__dot::before {
  width: ${card.dotnavSize}px;
  height: ${card.dotnavSize}px;
}`)
  }
  if (isPx(card.imageBottomMargin)) {
    // Overlay excluded: its media IS the card, and a bottom margin would
    // expose a bare card-background strip under the photo and scrim.
    parts.push(
      `${S} .hfse-property-card .hk-card-media-top { margin-bottom: ${card.imageBottomMargin}px; }`,
      `${TC} .hr-pc-media { margin-bottom: ${card.imageBottomMargin}px; }`,
    )
  }

  // ── Layout ──────────────────────────────────────────────────────────────
  if (card.blockOrder === 'image-meta-body') {
    // The card is already `hk-flex hk-flex-column`; assert it so the flag
    // cannot silently no-op if those utility classes ever change.
    parts.push(`${S} .hfse-property-card { display: flex; flex-direction: column; }
${S} .hfse-property-card .hfse-property-meta { order: 1; }
${S} .hfse-property-card .hfse-property-body { order: 2; }`)
  }
  // `order` needs a flex parent, and .hfse-property-meta is a plain block in
  // explorer.css — so specs-first and metaGap both have to establish one.
  const metaFlex = card.metaOrder === 'specs-first' || isPx(card.metaGap)
  if (metaFlex) {
    const gap = isPx(card.metaGap) ? ` gap: ${card.metaGap}px;` : ''
    parts.push(`${S} .hfse-property-meta { display: flex; flex-direction: column;${gap} }`)
    if (isPx(card.metaGap)) {
      // Flex `gap` is ADDITIVE with item margins (CSS Box Alignment) — it does
      // not neutralise them. explorer.css puts `margin-top: 10px` on BOTH meta
      // rows, so without this reset the control can never reach its own value:
      // metaGap 0 still renders 10px and metaGap 8 renders 18px. Both rows are
      // reset, not just the secondary: under metaOrder 'specs-first' the
      // secondary takes `order: -1` and meta-PRIMARY becomes the second row,
      // so its margin becomes the inter-row gap instead.
      //
      // Gated on metaGap rather than on metaFlex so that choosing 'specs-first'
      // alone does not silently change spacing. Setting a gap DOES also remove
      // the 10px above the first meta row (its separation from the card body):
      // once a "Gap Between Meta Rows" control is set it owns that spacing.
      parts.push(`${S} .hfse-property-meta > .hfse-property-meta-primary,
${S} .hfse-property-meta > .hfse-property-meta-secondary { margin-top: 0; }`)
    }
  }
  if (card.metaOrder === 'specs-first') {
    parts.push(`${S} .hfse-property-meta .hfse-property-meta-secondary { order: -1; }`)
  }
  if (card.metaLayout === 'stacked') {
    parts.push(`${S} .hfse-property-meta-primary {
  flex-direction: column;
  align-items: flex-start;
  justify-content: flex-start;
}`)
  }
  if (card.metaSpecStyle === 'text') {
    // The icons are simply not rendered in text mode (PropertyCard drops the
    // SVGs), so this half only lays the labels out inline with pipe rules.
    parts.push(`${S} .hfse-property-meta-secondary {
  justify-content: flex-start;
  flex-wrap: nowrap;
  white-space: nowrap;
  gap: 0;
}
${S} .hfse-property-meta-secondary > *:not(:last-child)::after {
  content: "|";
  color: var(--hfse-border, #d1d5db);
  font-weight: 400;
  margin: 0 10px;
}`)
  }

  // Template family layout (#902). The legacy "meta block" maps onto two body
  // children here — .hr-pc-location (meta-primary) and .hr-pc-meta
  // (meta-secondary) — with the heading and price between them in DOM order,
  // so the knobs compose through explicit flex orders instead of a container
  // swap. metaLayout and metaSpecStyle stay Legacy-only: the templates have no
  // type+location row (the chip owns type) and render text specs natively.
  {
    const blockAbove = card.blockOrder === 'image-meta-body'
    const specsFirst = card.metaOrder === 'specs-first'
    const priceFirst = card.priceOrder === 'before-title' && card.pricePosition !== 'absolute-bottom-right'
    // The two-row pair gate (fresh pass): specs-first is a SWAP and metaGap a
    // gap BETWEEN the location/specs rows — with either row hidden
    // (showLocation/showFeatures off) both knobs go inert instead of sinking
    // the lone survivor below the price. blockAbove is deliberately ungated:
    // lifting whichever meta rows exist is still its meaning.
    const pairBody = `${TC} .hr-pc-body:has(> .hr-pc-location):has(> .hr-pc-meta)`
    if (blockAbove || specsFirst || priceFirst) {
      // Flex only when an order knob needs it AND its own gate holds — a
      // flex host with every order idled would still stop body margins
      // collapsing for nothing (metaGap alone must not flip it either).
      // blockAbove is the one unconditional host: it orders whichever meta
      // rows exist. specsFirst rides its pair gate; priceFirst rides the
      // rendered-price gate (Detailed's pricerow shell renders for the view
      // link even when the price mode renders nothing).
      const hostSelectors: string[] = []
      if (blockAbove) {
        hostSelectors.push(`${TC} .hr-pc-body`)
      } else {
        if (specsFirst) hostSelectors.push(`${pairBody}`)
        if (priceFirst) hostSelectors.push(`${TC} .hr-pc-body:has(> .hr-pc-price, > .hr-pc-pricerow > .hr-pc-price)`)
      }
      parts.push(`${hostSelectors.join(', ')} { display: flex; flex-direction: column; }`)
      // A no-photo body renders the type chip in the flow; as a flex (or
      // grid) item it would stretch toward its 42% max-width instead of
      // shrink-wrapping (fresh pass).
      parts.push(`${TC} .hr-pc-body > .hr-pc-chip { align-self: flex-start; justify-self: start; }`)
    }
    // Card rating placement "location" (#1906): the rating row is the
    // location's next sibling in DOM order, but the orders below move the
    // location alone and would leave the row behind at order 0 (above the
    // sunk pair, or below the lifted block). The same order value keeps DOM
    // order inside the slot, so the row stays right under the location line.
    // Emitted only when the placement leaf is set, so every other widget's
    // stylesheet is unchanged.
    const ratingUnderLocation = card.ratingPlacement === 'location'
    if (blockAbove) {
      // Both meta rows lift above the heading, keeping their own order
      // (-4/-3), which leaves -2 free for a before-title price between the
      // lifted block and the heading.
      parts.push(`${TC} .hr-pc-body > .hr-pc-location { order: ${specsFirst ? -3 : -4}; }
${TC} .hr-pc-body > .hr-pc-meta { order: ${specsFirst ? -4 : -3}; }`)
      if (ratingUnderLocation) {
        parts.push(`${TC} .hr-pc-body > .hr-pc-location + .hr-pc-ratingrow { order: ${specsFirst ? -3 : -4}; }`)
      }
    } else if (specsFirst) {
      parts.push(`${pairBody} > .hr-pc-meta { order: 1; }
${pairBody} > .hr-pc-location { order: 2; }`)
      if (ratingUnderLocation) {
        parts.push(`${pairBody} > .hr-pc-location + .hr-pc-ratingrow { order: 2; }`)
      }
    }
    if (priceFirst) {
      // .hr-pc-pricerow is Detailed's wrapper (price + view link); Classic
      // renders the price directly in the body. The :has() gate keeps a
      // view-link-only row from jumping above the title when no price
      // renders (fresh pass HIGH).
      parts.push(`${TC} .hr-pc-body > .hr-pc-price, ${TC} .hr-pc-body > .hr-pc-pricerow:has(> .hr-pc-price) {
  order: -2;
  margin-top: 0;
  margin-bottom: 8px;
}`)
    }
    if (isPx(card.metaGap)) {
      // The knob owns the space BETWEEN the location row and the specs row
      // (codex r3): margin-top goes on whichever of the pair renders second —
      // specs by default, location under specs-first (in both its variants,
      // with and without blockAbove, location follows specs).
      const gapTarget = specsFirst ? '.hr-pc-location' : '.hr-pc-meta'
      parts.push(`${pairBody} > ${gapTarget} { margin-top: ${card.metaGap}px; }`)
    }
  }

  // ── Price placement ─────────────────────────────────────────────────────
  if (card.priceOrder === 'before-title' && card.pricePosition !== 'absolute-bottom-right') {
    // Inert when the price is out of flow, matching the toolkit (whose admin
    // panel greys this control in absolute mode).
    parts.push(`${S} .hfse-property-body { display: flex; flex-direction: column; }
${S} .hfse-property-body .hfse-property-price-container {
  order: -1;
  margin-top: 0;
  margin-bottom: 8px;
}`)
  }
  if (card.pricePosition === 'absolute-bottom-right') {
    // The specs row has to give the floated price its corner back, or the two
    // overlap — the toolkit ships this rule inside the same branch and the
    // port dropped it. `gap` is conditional on purpose: the text-spec block
    // above sets `gap: 0` at the SAME specificity and is pushed to `parts`
    // earlier, so an unconditional 14px here would win on source order and
    // double-space the pipe separators. padding-right reserves the price's
    // own footprint (140px min-width + the 16px right offset) so the rule
    // stops depending on how wide the spec row happens to render.
    const specGap = card.metaSpecStyle !== 'text' ? '\n  gap: 14px;' : ''
    parts.push(`${S} .hfse-property-card { position: relative; }
${S} .hfse-property-price-container {
  position: absolute;
  bottom: 16px;
  right: 16px;
  min-width: 140px;
  text-align: right;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  z-index: 1;
  margin-top: 0;
}
${S} .hfse-property-price,
${S} .hfse-property-price-total {
  text-align: right;
  width: 100%;
}
${S} .hfse-property-meta-secondary {
  justify-content: flex-start;${specGap}
  padding-right: 156px;
}`)
    // Template family: an in-flow two-column grid, NOT an absolute float
    // (codex r2+r3). A fixed padding reservation can't survive every
    // arrangement × font-size combination, and a short meta row can't cap the
    // height of a multi-line quote — an absolute price could rise over the
    // content above it. As a grid cell the price gets a real column whose row
    // height is max(meta, price), the specs row needs no padding reservation
    // (and pays none when the price mode renders nothing — both :has() gates),
    // and the static cell stays below the stretched link, so no pointer-events
    // dance. Detailed's whole pricerow (price + view link) takes the cell,
    // consistent with the accepted pricerow-as-a-unit behavior. The `order`
    // knobs keep working: grid auto-placement honors `order`, and this
    // display:grid outranks the order-knobs' display:flex on source order at
    // equal specificity. `span 9` bottoms the cell: surplus implicit rows are
    // empty and collapse to zero height.
    // The gate requires a rendered PRICE, not merely the pricerow shell —
    // Detailed renders .hr-pc-pricerow (for the view link) even when
    // GridQuotePrice returns null, and priceDisplay:none must not pay for a
    // price column (codex r4). No meta-row gate (fresh pass 2): that was a
    // holdover from the absolute-positioning era — as a grid the price gets
    // a real cell whatever rows exist, so the knob keeps working with the
    // specs row hidden (heading/location fill the left column).
    // fit-content(60%) caps the price column so a narrow card (6-across
    // grids) wraps the price text instead of collapsing the left column or
    // overflowing — which is also why there is no fixed min-width here.
    const cellHost = `${TC} .hr-pc-body:has(> .hr-pc-price, > .hr-pc-pricerow > .hr-pc-price)`
    parts.push(`${cellHost} {
  display: grid;
  grid-template-columns: minmax(0, 1fr) fit-content(60%);
  column-gap: 12px;
}
${cellHost} > * { grid-column: 1; }
${cellHost} > .hr-pc-chip { justify-self: start; }
${cellHost} > .hr-pc-price, ${cellHost} > .hr-pc-pricerow {
  grid-column: 2;
  grid-row: 1 / span 9;
  align-self: end;
  justify-self: end;
  text-align: right;
  margin: 0;
}`)
  }
  if (card.priceArrangement && card.priceArrangement !== 'default') {
    // Reorders the container's siblings and drops the middle dot, exactly as
    // the toolkit does — the widget renders the same four class names.
    //
    // The `:has()` guards below are load-bearing, NOT decoration. PropertyCard
    // emits ten `.hfse-property-price-container` branches and only three of
    // them carry a `.hfse-property-price-total` child (the two full-quote
    // per-night/average branches and the calendar-derived per-stay branch).
    // The other seven — including the shipped default `priceDisplay.noDates:
    // 'lowest'` pre-search card ("From $200") — hold `.hfse-property-price`
    // alone. Hiding the sibling unconditionally therefore blanks the ONLY
    // price node in those seven and the visitor sees an empty box. Each hiding
    // rule is scoped to containers that actually own the replacement node, so
    // an arrangement can never erase a container's last price.
    parts.push(`${S} .hfse-property-price-container { display: flex; flex-direction: column; }
${S} .hfse-property-price-container .hfse-property-price-sep { display: none; }`)
    if (card.priceArrangement === 'per-night-only') {
      parts.push(`${S} .hfse-property-price-container .hfse-property-price { order: 1; }
${S} .hfse-property-price-container:has(.hfse-property-price) .hfse-property-price-total { display: none; }
${S} .hfse-property-price-container .hfse-property-tax-note { display: none; }`)
    }
    if (card.priceArrangement === 'per-night-then-total') {
      parts.push(`${S} .hfse-property-price-container .hfse-property-price { order: 1; }
${S} .hfse-property-price-container .hfse-property-price-total { order: 2; }
${S} .hfse-property-price-container .hfse-property-tax-note { display: none; }`)
    }
    if (card.priceArrangement === 'total-only') {
      parts.push(`${S} .hfse-property-price-container .hfse-property-price-total { order: 1; }
${S} .hfse-property-price-container:has(.hfse-property-price-total) .hfse-property-price { display: none; }
${S} .hfse-property-price-container .hfse-property-tax-note { display: none; }`)
    }
    // Template family: grid-quote-price.tsx wraps the quote line's per-night
    // cluster (.hr-pc-price__pernight) and tax words (.hr-pc-price__taxnote /
    // __note--tax) for exactly these rules. Same :has() discipline: a hiding
    // rule fires only inside a line that actually owns the replacement node,
    // so the seven single-price branches (plain .hr-pc-price with no __line)
    // can never lose their only price.
    parts.push(`${T} .hr-pc-price__sep { display: none; }
${T} .hr-pc-price__taxnote, ${T} .hr-pc-price__notesep, ${T} .hr-pc-price__note--tax { display: none; }`)
    if (card.priceArrangement === 'per-night-only') {
      parts.push(`${T} .hr-pc-price__line:has(.hr-pc-price__pernight) .hr-pc-price__total { display: none; }`)
    }
    if (card.priceArrangement === 'per-night-then-total') {
      // Column-stacks ONLY a line that owns both halves; the spans stretch
      // full-width so text-align (left in flow, right when floated) keeps
      // working. The pair-guard is load-bearing: a total-mode line is
      // `<b>$X</b> total` with loose text, and flex would promote that text
      // node to its own anonymous row — "$1,400" over "total".
      parts.push(`${T} .hr-pc-price__line:has(.hr-pc-price__pernight):has(.hr-pc-price__total) { display: flex; flex-direction: column; }`)
    }
    if (card.priceArrangement === 'total-only') {
      parts.push(`${T} .hr-pc-price__line:has(.hr-pc-price__total) .hr-pc-price__pernight { display: none; }`)
    }
  }
  if (card.showTaxLine === false) {
    parts.push(
      `${S} .hfse-property-tax-note { display: none; }`,
      `${T} .hr-pc-price__taxnote, ${T} .hr-pc-price__notesep, ${T} .hr-pc-price__note--tax { display: none; }`,
    )
  }

  // ── Dividers ────────────────────────────────────────────────────────────
  if (card.showTopDivider === true) {
    // Same wrapper level both times: .hfse-property-container and
    // .hr-property-card are each the grid's .property-item.
    parts.push(`${S} .hfse-property-container {
  border-top: 1px solid var(--hfse-border, #eee);
  padding-top: 20px;
}`)
    parts.push(`${T} {
  border-top: 1px solid var(--hfse-border, #eee);
  padding-top: 20px;
}`)
  }
  if (card.showSecondaryMetaDivider === false) {
    // .hr-pc-meta natively carries the same border-top divider the legacy
    // specs row does, so the off-switch maps one to one.
    parts.push(
      `${S} .hfse-property-meta-secondary { border-top: none; padding-top: 0; }`,
      `${T} .hr-pc-meta { border-top: none; padding-top: 0; }`,
    )
  }

  // ── Spacing ─────────────────────────────────────────────────────────────
  if (isPx(card.bodyPaddingX)) {
    // The meta block carries its own 20px gutter in explorer.css; moving it
    // with the body keeps the two columns of card text aligned.
    parts.push(`${S} .hfse-property-card .hk-card-body,
${S} .hfse-property-card .hfse-property-meta {
  padding-left: ${card.bodyPaddingX}px;
  padding-right: ${card.bodyPaddingX}px;
}`)
    parts.push(`${T} .hr-pc-body {
  padding-left: ${card.bodyPaddingX}px;
  padding-right: ${card.bodyPaddingX}px;
}`)
  }
  if (isPx(card.bodyPaddingY)) {
    parts.push(`${S} .hfse-property-card .hk-card-body {
  padding-top: ${card.bodyPaddingY}px;
  padding-bottom: ${card.bodyPaddingY}px;
}`)
    parts.push(`${T} .hr-pc-body {
  padding-top: ${card.bodyPaddingY}px;
  padding-bottom: ${card.bodyPaddingY}px;
}`)
  }

  // ── Load More (#872 close-out) ──────────────────────────────────────────
  // Grid-level, not card-markup-level: PropertyGrid renders .hfse-load-more-btn
  // under every card.design, so this is the one knob group that can never be
  // inert on a non-Legacy design.
  if (isHex(card.loadMoreBg)) {
    // The resting button is bordered (1px --hfse-border); a colored fill
    // against the old gray border reads as a bug, so the border follows the
    // fill.
    parts.push(`${S} .hfse-load-more-btn { background: ${card.loadMoreBg}; border-color: ${card.loadMoreBg}; }`)
  }
  if (isHex(card.loadMoreText)) {
    parts.push(`${S} .hfse-load-more-btn { color: ${card.loadMoreText}; }`)
  }
  if (isHex(card.loadMoreHover)) {
    // explorer.css hovers via filter: brightness(0.95), which would stack on
    // top of the explicit hover color — turn it off when the color is set.
    parts.push(`${S} .hfse-load-more-btn:hover { background: ${card.loadMoreHover}; border-color: ${card.loadMoreHover}; filter: none; }`)
  }
  if (isPx(card.loadMoreRadius)) {
    parts.push(`${S} .hfse-load-more-btn { border-radius: ${card.loadMoreRadius}px; }`)
  }
  if (card.loadMoreAlign === 'left' || card.loadMoreAlign === 'right') {
    parts.push(`${S} .hfse-load-more-container { text-align: ${card.loadMoreAlign}; }`)
  }

  return parts.join('\n')
}

/**
 * The Google CSS link a skin title preset needs SERVER-side (#1316, #902): the
 * preset is a font-family value only, so without this the SSR'd markup paints
 * the preset's system fallback until the client bundle hydrates and WebFont's
 * extraFamily (#1219/#1304, CSR-only) takes over. Deduped against the widget
 * font — the same rule WebFont.tsx applies. Tolerates parsed OR raw settings
 * (the renderer parses before getStaticAssets since #1173, older callers pass
 * raw): an unknown preset maps to nothing.
 */
export function cardSkinTitleFontCssUrls(
  card: { titleFontFamily?: string } | undefined,
  widgetFontFamily: string | undefined,
): string[] {
  const titleGoogleFont = getCardSkinTitleGoogleFont(card?.titleFontFamily)
  if (!titleGoogleFont) return []
  // Dedupe on the RESOLVED link, not the stored family: getFontCssUrl maps a
  // null/''/absent widget font to Inter (#1171), so comparing raw strings let a
  // `modern-sans` title on a widget with no `font` key ship two identical
  // Inter stylesheets.
  const url = getFontCssUrl(titleGoogleFont)
  return url === getFontCssUrl(widgetFontFamily) ? [] : [url]
}
